import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { requisitionsApi, ebosApi, openAuthedInNewTabPost } from '../api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import SavedFiltersToolbar from '../components/SavedFiltersToolbar';
import { ChevronRight, Loader2, Pencil, Trash2, ArrowUpDown, Search, Download, Printer } from 'lucide-react';
import { fmtIST } from '../utils/datetime';

const PRINTABLE_STATUSES = new Set(['Fulfilled', 'Shipped', 'Delivered']);

const SORT_OPTIONS = [
  { key: 'date-desc', label: 'Date · newest first' },
  { key: 'date-asc', label: 'Date · oldest first' },
  { key: 'fulfilled-desc', label: 'Fulfilled date · newest first' },
  { key: 'fulfilled-asc', label: 'Fulfilled date · oldest first' },
  { key: 'delivered-desc', label: 'Delivered date · newest first' },
  { key: 'delivered-asc', label: 'Delivered date · oldest first' },
  { key: 'ebo-asc', label: 'EBO · A → Z' },
  { key: 'ebo-desc', label: 'EBO · Z → A' },
  { key: 'qty-desc', label: 'Total qty · high → low' },
  { key: 'qty-asc', label: 'Total qty · low → high' },
];

const totalQty = (r) => (r.lines || []).reduce((s, l) => s + (Number(l.qty) || 0), 0);
/** Parse a DD/MM/YYYY (or legacy DD Mon YYYY / YYYY-MM-DD) into a JS Date, or null. */
function parseDate(s) {
  if (!s) return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (m) return new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  const m2 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());     // ISO date from HTML5 <input type=date>
  if (m2) return new Date(Date.UTC(+m2[1], +m2[2] - 1, +m2[3]));
  const d = new Date(s);                                     // fallback (legacy "03 Aug 2026" / ISO datetime)
  return isNaN(d) ? null : d;
}
/** Render an ISO datetime OR a DD/MM/YYYY string as DD/MM/YYYY; '—' if unset. */
function fmtDDMMYYYY(v) {
  if (!v) return '—';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;
  const d = parseDate(v);
  if (!d) return '—';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}
function cmpDates(a, b) {
  const da = parseDate(a); const db = parseDate(b);
  if (!da && !db) return 0;
  if (!da) return 1;   // rows with unparseable dates sink
  if (!db) return -1;
  return da.getTime() - db.getTime();
}
/** Direction-aware date comparator that ALWAYS sinks null/unset dates to the bottom,
 * regardless of asc/desc. Plain negation of cmpDates flips the null-sink behaviour. */
function cmpDatesDir(a, b, dir) {
  const da = parseDate(a); const db = parseDate(b);
  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;
  return dir === 'asc' ? da.getTime() - db.getTime() : db.getTime() - da.getTime();
}
const monthKey = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return '';
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`; // YYYY-MM
};
const monthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const d = new Date(`${ym}-01T00:00:00Z`);
  return isNaN(d) ? ym : d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export default function AllRequisitions() {
  const { user, theme } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reqs, setReqs] = useState([]);
  const [ebos, setEbos] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters + sort — accept `?ebo=CODE` from a dashboard drill-down link
  const [q, setQ] = useState('');
  const [ebo, setEbo] = useState(searchParams.get('ebo') || 'all');
  const [status, setStatus] = useState('all');
  const [zone, setZone] = useState('all');
  const [state, setState] = useState('all');
  const [month, setMonth] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('date-desc');

  // Keep the EBO filter in sync when the query-param changes (e.g. drilling
  // from one branch to another via the Overview card).
  useEffect(() => {
    const paramEbo = searchParams.get('ebo');
    if (paramEbo && paramEbo !== ebo) setEbo(paramEbo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Saved-filter templates — per-user server-side so ops can reuse a slice
  // (e.g. "North Q1 slow branches") across sessions and devices.
  const currentFilters = useMemo(() => ({ q, ebo, status, zone, state, month, dateFrom, dateTo, sort }),
    [q, ebo, status, zone, state, month, dateFrom, dateTo, sort]);

  const applyTemplate = (f) => {
    setQ(f.q || '');
    setEbo(f.ebo || 'all');
    setStatus(f.status || 'all');
    setZone(f.zone || 'all');
    setState(f.state || 'all');
    setMonth(f.month || 'all');
    setDateFrom(f.dateFrom || '');
    setDateTo(f.dateTo || '');
    setSort(f.sort || 'date-desc');
  };

  const load = async () => {
    setLoading(true);
    try {
      const [rs, es] = await Promise.all([requisitionsApi.list(), ebosApi.list()]);
      setReqs(rs); setEbos(es);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // ebo lookup for zone/state per requisition
  const eboLookup = useMemo(() => {
    const m = new Map();
    for (const e of ebos) m.set(e.code, e);
    return m;
  }, [ebos]);

  const zoneOptions = useMemo(() => [...new Set(ebos.map(e => e.zone).filter(Boolean))].sort(), [ebos]);
  const stateOptions = useMemo(() => [...new Set(ebos.map(e => e.state).filter(Boolean))].sort(), [ebos]);
  const monthOptions = useMemo(() => {
    const set = new Set(reqs.map(r => monthKey(r.date)).filter(Boolean));
    return [...set].sort((a, b) => b.localeCompare(a)); // newest month first
  }, [reqs]);

  const list = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    let filtered = reqs.filter(r => {
      if (ebo !== 'all' && r.eboBranchCode !== ebo) return false;
      if (status !== 'all' && r.status !== status) return false;
      const e = eboLookup.get(r.eboBranchCode);
      if (zone !== 'all' && (e?.zone || '') !== zone) return false;
      if (state !== 'all' && (e?.state || '') !== state) return false;
      if (month !== 'all' && monthKey(r.date) !== month) return false;
      // Date range: parse both inputs and stored date, compare as UTC dates
      const rDate = parseDate(r.date);
      if (dateFrom) {
        const fromD = parseDate(dateFrom);
        if (fromD && rDate && rDate < fromD) return false;
      }
      if (dateTo) {
        const toD = parseDate(dateTo);
        if (toD && rDate && rDate > toD) return false;
      }
      if (qLower && !`${r.id || ''} ${r.ebo || ''} ${r.eboBranchCode || ''}`.toLowerCase().includes(qLower)) return false;
      return true;
    });
    const strCmp = (a, b, key, dir = 'asc') => {
      const av = (a[key] || '').toString().toLowerCase();
      const bv = (b[key] || '').toString().toLowerCase();
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    };
    switch (sort) {
      case 'date-asc': filtered = [...filtered].sort((a, b) => cmpDates(a.date, b.date) || a.id.localeCompare(b.id)); break;
      case 'fulfilled-asc': filtered = [...filtered].sort((a, b) => cmpDatesDir(a.fulfilledAt, b.fulfilledAt, 'asc') || a.id.localeCompare(b.id)); break;
      case 'fulfilled-desc': filtered = [...filtered].sort((a, b) => cmpDatesDir(a.fulfilledAt, b.fulfilledAt, 'desc') || b.id.localeCompare(a.id)); break;
      case 'delivered-asc': filtered = [...filtered].sort((a, b) => cmpDatesDir(a.deliveredAt, b.deliveredAt, 'asc') || a.id.localeCompare(b.id)); break;
      case 'delivered-desc': filtered = [...filtered].sort((a, b) => cmpDatesDir(a.deliveredAt, b.deliveredAt, 'desc') || b.id.localeCompare(a.id)); break;
      case 'ebo-asc': filtered = [...filtered].sort((a, b) => strCmp(a, b, 'ebo', 'asc')); break;
      case 'ebo-desc': filtered = [...filtered].sort((a, b) => strCmp(a, b, 'ebo', 'desc')); break;
      case 'qty-asc': filtered = [...filtered].sort((a, b) => totalQty(a) - totalQty(b)); break;
      case 'qty-desc': filtered = [...filtered].sort((a, b) => totalQty(b) - totalQty(a)); break;
      default: filtered = [...filtered].sort((a, b) => -cmpDates(a.date, b.date) || b.id.localeCompare(a.id));
    }
    return filtered;
  }, [reqs, eboLookup, q, ebo, status, zone, state, month, dateFrom, dateTo, sort]);

  const clearFilters = () => {
    setQ(''); setEbo('all'); setStatus('all'); setZone('all'); setState('all');
    setMonth('all'); setDateFrom(''); setDateTo(''); setSort('date-desc');
  };
  const activeFilterCount = [
    q, ebo !== 'all', status !== 'all', zone !== 'all', state !== 'all',
    month !== 'all', dateFrom, dateTo,
  ].filter(Boolean).length;

  const linesDelivered = (r) => (r.lines || []).filter(l => l.lineStatus === 'Delivered').length;
  const canModifyOrDelete = user?.role === 'SuperAdmin';

  const remove = async (r) => {
    if (!window.confirm(`Delete requisition ${r.id}? This cannot be undone.`)) return;
    try { await requisitionsApi.remove(r.id); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const summaryQty = useMemo(() => list.reduce((s, r) => s + totalQty(r), 0), [list]);

  const [downloadingFiltered, setDownloadingFiltered] = useState(false);
  const downloadFiltered = async () => {
    if (list.length === 0 || downloadingFiltered) return;
    setDownloadingFiltered(true);
    try {
      const blob = await requisitionsApi.exportBulk(list.map(r => r.id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requisitions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingFiltered(false);
    }
  };

  // -------- Waybill batch print --------
  // Selection is scoped to the currently-visible list — filter changes drop
  // now-hidden rows automatically so the user never accidentally batch-prints
  // something they can no longer see.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const visibleSelectableIds = useMemo(
    () => list.filter(r => PRINTABLE_STATUSES.has(r.status)).map(r => r.id),
    [list],
  );
  const effectiveSelection = useMemo(() => {
    const s = new Set();
    for (const id of visibleSelectableIds) if (selectedIds.has(id)) s.add(id);
    return s;
  }, [selectedIds, visibleSelectableIds]);
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const allSelected = visibleSelectableIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        visibleSelectableIds.forEach(id => next.delete(id));
      } else {
        visibleSelectableIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected = visibleSelectableIds.length > 0
    && visibleSelectableIds.every(id => selectedIds.has(id));

  const [printingBatch, setPrintingBatch] = useState(false);
  const printBatch = async () => {
    if (effectiveSelection.size === 0 || printingBatch) return;
    setPrintingBatch(true);
    try {
      // Preserve list-order so page 1 matches row-1.
      const ids = visibleSelectableIds.filter(id => effectiveSelection.has(id));
      const { included, skipped } = await openAuthedInNewTabPost(
        requisitionsApi.waybillsBulkUrl(), { ids },
      );
      if (skipped && skipped.length) {
        alert(`Printed ${included} waybill(s). Skipped (not printable): ${skipped.join(', ')}`);
      }
      clearSelection();
    } catch (e) {
      alert(e.message || 'Batch print failed');
    } finally {
      setPrintingBatch(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="All requisitions"
        subtitle={<span><span className="font-semibold text-[#232323]">{list.length}</span> of {reqs.length} requisitions · {summaryQty} total units in view · click a requisition number to open</span>}
        actions={
          <div className="flex items-center gap-2">
            {effectiveSelection.size > 0 && (
              <button
                data-testid="req-print-waybills-batch-btn"
                onClick={printBatch}
                disabled={printingBatch}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] transition-colors disabled:opacity-50"
                title={`Merge ${effectiveSelection.size} waybill(s) into one printable PDF`}
              >
                {printingBatch ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                Print waybills ({effectiveSelection.size})
              </button>
            )}
            <button
              data-testid="req-download-filtered-btn"
              onClick={downloadFiltered}
              disabled={list.length === 0 || downloadingFiltered}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-40"
              style={{ background: theme.primary }}
              title={list.length === 0 ? 'No rows to export' : `Download ${list.length} filtered requisition(s) as Excel`}
            >
              {downloadingFiltered ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              Download filtered ({list.length})
            </button>
          </div>
        }
      />

      {/* Row 1: search + dropdown filters */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="relative min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6f5c]" />
          <input data-testid="req-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search req id / EBO name / branch code…" className="fm-focus border border-[#E7E2D3] rounded-md pl-9 pr-3 py-2 text-[13px] bg-white w-full" />
        </div>
        <select data-testid="req-filter-ebo" value={ebo} onChange={(e) => setEbo(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] min-w-[180px] bg-white">
          <option value="all">All EBOs</option>
          {ebos.map(e => <option key={e.code} value={e.code}>{e.name}</option>)}
        </select>
        <select data-testid="req-filter-zone" value={zone} onChange={e => setZone(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">All zones</option>
          {zoneOptions.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <select data-testid="req-filter-state" value={state} onChange={e => setState(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">All states</option>
          {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="req-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">All statuses</option>
          <option>Requested</option>
          <option>Accepted</option>
          <option>PartiallyAccepted</option>
          <option>Held</option>
          <option>Rejected</option>
          <option>Fulfilled</option>
          <option>Shipped</option>
          <option>Delivered</option>
        </select>
        <div className="flex items-center gap-1 pl-1">
          <ArrowUpDown size={13} className="text-[#7a6f5c]" />
          <select data-testid="req-sort" value={sort} onChange={e => setSort(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        {/* Saved filter templates */}
        <div className="ml-auto" data-testid="filter-templates-block">
          <SavedFiltersToolbar
            scope="requisitions"
            filters={currentFilters}
            onApply={applyTemplate}
            theme={theme}
          />
        </div>
      </div>

      {/* Row 2: date range + month filter + clear */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="req-filter-month" value={month} onChange={e => setMonth(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">Any month</option>
          {monthOptions.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <label className="text-[12px] text-[#7a6f5c] ml-2">From</label>
        <input data-testid="req-filter-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="fm-focus border border-[#E7E2D3] rounded-md px-2 py-2 text-[13px] bg-white" />
        <label className="text-[12px] text-[#7a6f5c]">To</label>
        <input data-testid="req-filter-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="fm-focus border border-[#E7E2D3] rounded-md px-2 py-2 text-[13px] bg-white" />
        {activeFilterCount > 0 && (
          <button data-testid="req-clear-filters" onClick={clearFilters} className="text-[12px] px-2 py-1 rounded border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] text-[#7a6f5c] ml-2">Clear filters · {activeFilterCount}</button>
        )}
      </div>

      <div className="fm-card fm-scroll-x">
        <table className="w-full text-[13.5px]" data-testid="req-table">
          <thead>
            <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3] whitespace-nowrap">
              <th className="pl-4 pr-1 py-3 font-medium w-8">
                <input
                  type="checkbox"
                  data-testid="req-select-all-checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  disabled={visibleSelectableIds.length === 0}
                  title={
                    visibleSelectableIds.length === 0
                      ? 'No printable requisitions in view'
                      : allVisibleSelected
                        ? `Clear selection (${visibleSelectableIds.length})`
                        : `Select all printable in view (${visibleSelectableIds.length})`
                  }
                  className="cursor-pointer accent-[#9C3D54] disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </th>
              <th className="px-4 py-3 font-medium">Requisition No.</th>
              <th className="px-4 py-3 font-medium">EBO</th>
              <th className="px-4 py-3 font-medium">Zone · State</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Fulfilled</th>
              <th className="px-4 py-3 font-medium">Delivered</th>
              <th className="px-4 py-3 font-medium text-center">SKUs</th>
              <th className="px-4 py-3 font-medium text-center">Total Qty</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-5 py-10 text-center text-[#7a6f5c]"><Loader2 className="inline animate-spin" size={18} /></td></tr>
            )}
            {!loading && list.length === 0 && (
              <tr><td colSpan={11} className="px-5 py-10 text-center text-[#7a6f5c]">
                {reqs.length === 0 ? 'No requisitions yet.' : 'No requisitions match those filters.'}
              </td></tr>
            )}
            {!loading && list.map(r => {
              const e = eboLookup.get(r.eboBranchCode);
              const printable = PRINTABLE_STATUSES.has(r.status);
              const isSelected = selectedIds.has(r.id);
              return (
              <tr key={r.id} className="fm-row border-t border-[#EFEADC] align-top">
                <td className="pl-4 pr-1 py-4 w-8">
                  <input
                    type="checkbox"
                    data-testid={`req-select-checkbox-${r.id}`}
                    checked={isSelected && printable}
                    onChange={() => toggleOne(r.id)}
                    disabled={!printable}
                    title={printable
                      ? (isSelected ? 'Unselect for batch print' : 'Add to batch waybill print')
                      : 'Waybill is only available for Fulfilled / Shipped / Delivered'}
                    className="cursor-pointer accent-[#9C3D54] disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-4 py-4 font-mono text-[13px] whitespace-nowrap">
                  <Link to={`/app/requisitions/${encodeURIComponent(r.id)}`} className="underline" style={{ color: theme.primary }}>{r.id}</Link>
                </td>
                <td className="px-4 py-4 min-w-[220px]">
                  <div className="text-[14px]">{r.ebo}</div>
                  <div className="text-[11px] text-[#7a6f5c] font-mono">{r.eboBranchCode}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-[12.5px] text-[#7a6f5c]">{e?.zone || '—'}{e?.state ? ` · ${e.state}` : ''}</td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {r.date}
                  {r.isHistorical && (
                    <span
                      data-testid={`req-historical-badge-${r.id}`}
                      title={`Backfilled by ${r.backdatedByName || r.backdatedByEmail || 'unknown'}`}
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F7E9CC] text-[#8B6A1F] border border-[#E8D28C]"
                    >
                      Historical
                    </span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-[12.5px]" data-testid={`req-fulfilled-${r.id}`}>{fmtIST(r.fulfilledAt) || '—'}</td>
                <td className="px-4 py-4 whitespace-nowrap text-[12.5px]" data-testid={`req-delivered-${r.id}`}>{fmtIST(r.deliveredAt) || '—'}</td>
                <td className="px-4 py-4 text-center">{r.lines.length}</td>
                <td className="px-4 py-4 text-center">{totalQty(r)}</td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    <span className="text-[11.5px] text-[#7a6f5c]">{linesDelivered(r)}/{r.lines.length} delivered</span>
                  </div>
                </td>
                <td className="px-4 py-4 text-right pr-6 whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    <Link to={`/app/requisitions/${encodeURIComponent(r.id)}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]"><ChevronRight size={12} /> Open</Link>
                    {canModifyOrDelete && (
                      <>
                        <button data-testid={`req-edit-${r.id}`} onClick={() => navigate(`/app/requisitions/${encodeURIComponent(r.id)}?edit=1`)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]"><Pencil size={12} /> Modify</button>
                        <button data-testid={`req-delete-${r.id}`} onClick={() => remove(r)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]" style={{ color: '#B84A4A' }}><Trash2 size={12} /> Delete</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
